import React, { useEffect, useState } from 'react';
import { useAuth } from '@/config/auth/AuthProvider';
import { Button, Popover } from '@douyinfe/semi-ui';
import classNames from 'classnames';
import { Icon, Toast } from '@/components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AccountApi from '@/api/AccountApi';
import _ from 'lodash';
import { settingBillingThunk } from '@/store/reducers/settingBillingReducer';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  BILLING_TAB_PARAM,
  BILLING_TABS,
  getBillingPath
} from '../../config/billing';

type MenuItem = {
  name: string,
  icon?: string,
  iconNode?: React.ReactNode,
  active?: boolean,
  onClick: () => void
}

function Menu(): React.ReactElement {
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('translation');
  const isBillingPage = location.pathname === '/settings/billing';
  const activeBillingTab = new URLSearchParams(location.search).get(BILLING_TAB_PARAM);
  const isCreditTab = activeBillingTab === BILLING_TABS.credit;
  const isUnlimitedCreditsPlan = useAppSelector((state) => state.settingBilling.creditStatus?.plan.credit_total === '-1');

  useEffect(() => {
    dispatch(settingBillingThunk.getCreditStatus());
  }, [dispatch]);

  const creditsIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g clipPath="url(#credits-menu-icon)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.5862 1.41481C7.36726 0.633914 8.63333 0.633815 9.41432 1.41481L14.5862 6.58669C15.3667 7.36772 15.3669 8.6339 14.5862 9.41481L9.41432 14.5867C8.63341 15.3674 7.36723 15.3672 6.5862 14.5867L1.41432 9.41481C0.633326 8.63381 0.633427 7.36775 1.41432 6.58669L6.5862 1.41481ZM8.00026 6.00075C6.89569 6.00075 6.00026 6.89618 6.00026 8.00075C6.00042 9.10518 6.89579 10.0008 8.00026 10.0008C9.10459 10.0006 10.0001 9.10508 10.0003 8.00075C10.0003 6.89628 9.10469 6.00091 8.00026 6.00075Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="credits-menu-icon">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  const handleLogout = async (): Promise<void> => {
    const response = await AccountApi.logout();
    if (response.data.code === 200) {
      auth.setCurrentUser(null);
    }

    navigate('/login');
    Toast.toastResponse(response);
  };

  const menu: MenuItem[] = [
    {
      name: t('common.settings'),
      icon: 'navigation/settings',
      active: isBillingPage && (!isCreditTab || isUnlimitedCreditsPlan),
      onClick: () => {
        navigate(getBillingPath(BILLING_TABS.planBilling));
      }
    },
    {

      name: t('common.logout'),
      icon: 'navigation/logout',
      onClick: handleLogout
    }
  ];

  if (!isUnlimitedCreditsPlan) {
    menu.unshift({
      name: t('navigation.settings.credit'),
      iconNode: creditsIcon,
      active: isBillingPage && isCreditTab,
      onClick: () => {
        navigate(getBillingPath(BILLING_TABS.credit));
      }
    });
  }

  return (
    <div className={classNames('flex-column', 'menu', 'relative')}>
      {
        _.map(menu, (item) => (
          <Button
            key={item.name}
            theme="borderless"
            type="secondary"
            className={classNames('btn-menu-item', {
              active: item.active,
              'btn-menu-item-credits': !item.icon && item.iconNode
            })}
            onClick={() => {
              item?.onClick();
            }}
            icon={item.iconNode ?? <Icon icon={item.icon ?? ''} className="icon" />}
          >
            {item.name}
          </Button>
        ))
      }
      <div className="w-4 h-4 absolute -left-[6px] top-12">
        <svg width="6" height="12" viewBox="0 0 6 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M-2.62268e-07 6L6 0.803848L6 11.1962L-2.62268e-07 6Z" fill="#252B2E" />
        </svg>
      </div>
    </div>
  );
}

export default function UserMenu(): React.ReactElement {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  if (!user) {
    return <div />;
  }

  return (
    <Popover
      content={<Menu />}
      clickToHide
      visible={visible}
      position="rightBottom"
      trigger="click"
      className="menu-popover bg-[#252B2E]"
      onVisibleChange={setVisible}
    >
      <div
        className={`flex border-t border-solid border-[#252B2E] py-6 pr-5 pl-[27px] cursor-pointer
         transition-all ${visible ? 'bg-[#373F46]' : 'bg-[#191E21]'} relative hover:bg-[#373F46]`}
      >
        <Icon icon="user/default-avatar" className="w-[30px] h-[30px] mr-2" />
        <div className="flex-1 overflow-hidden">
          <p className="text-[#D1D7DD]">{user.username}</p>
          <p className="text-[#76808A] overflow-hidden text-ellipsis">{user.email}</p>
        </div>

        <Icon
          icon="common/arrow-left-triangle"
          className={`text-[#A1AAB0] absolute top-6 right-4 transition-all ${visible ? 'rotate-180' : ''}`}
        />
      </div>
    </Popover>
  );
}
